// Footer4.js - Contains functions for creating a dark-themed multi-column footer section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Footer section with a dark theme, multiple columns, and a newsletter
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the footer is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The footer elements
 */
export const createFooterElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const footerSectionClass = 'footer4-section';
  const footerContainerClass = 'footer4-container';
  const footerMainClass = 'footer4-main';
  const footerColumnClass = 'footer4-column';
  const footerLogoContainerClass = 'footer4-logo-container';
  const footerLogoClass = 'footer4-logo';
  const footerLogoTextClass = 'footer4-logo-text';
  const footerDescriptionClass = 'footer4-description';
  const footerAppLinksClass = 'footer4-app-links';
  const footerHeadingClass = 'footer4-heading';
  const footerLinkClass = 'footer4-link';
  const footerLinkListClass = 'footer4-link-list';
  const footerContactClass = 'footer4-contact';
  const footerContactItemClass = 'footer4-contact-item';
  const footerNewsletterClass = 'footer4-newsletter';
  const footerInputClass = 'footer4-input';
  const footerButtonClass = 'footer4-button';
  const footerSocialClass = 'footer4-social';
  const footerSocialIconClass = 'footer4-social-icon';
  const footerBottomClass = 'footer4-bottom';
  const footerDividerClass = 'footer4-divider';
  const footerCopyrightClass = 'footer4-copyright';
  const footerTermsClass = 'footer4-terms';
  
  // Desktop Styles (default)
  const footerSectionStyles = {
    position: 'relative',
    paddingTop: '60px',
    paddingBottom: '30px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#1e1e1e',
    color: '#ffffff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const footerContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    maxWidth: '1200px',
    width: '100%',
    marginTop: '0',
    marginBottom: '0',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '40px'
  };
  
  const footerMainStyles = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: '30px'
  };
  
  const footerColumnStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    flex: '1',
    minWidth: '200px'
  };
  
  const footerLogoContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  };
  
  const footerLogoStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };
  
  const footerLogoTextStyles = {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#ffffff'
  };
  
  const footerDescriptionStyles = {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#d0d0d0',
    marginTop: '10px',
    marginBottom: '20px'
  };
  
  const footerAppLinksStyles = {
    display: 'flex',
    gap: '15px',
    marginTop: '15px'
  };
  
  const footerHeadingStyles = {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#ffd700', // Golden color as in the reference
    marginBottom: '20px'
  };
  
  const footerLinkListStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  };
  
  const footerLinkStyles = {
    fontSize: '14px',
    color: '#ffffff',
    textDecoration: 'none',
    transition: 'color 0.2s ease'
  };
  
  const footerContactStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  };
  
  const footerContactItemStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    fontSize: '14px',
    color: '#d0d0d0'
  };
  
  const footerNewsletterStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    position: 'relative',
    width: '100%'
  };
  
  const footerInputStyles = {
    height: '44px',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '15px',
    paddingRight: '50px',
    borderRadius: '4px',
    border: 'none',
    fontSize: '14px',
    backgroundColor: '#333333',
    color: '#ffffff',
    width: '100%',
    position: 'relative'
  };
  
  const footerButtonStyles = {
    position: 'absolute',
    right: '5px',
    top: '50%',
    transform: 'translateY(-50%)',
    minWidth: '34px',
    height: '34px',
    paddingTop: '0',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
    backgroundColor: '#ffd700',
    color: '#000000',
    border: 'none',
    borderRadius: '4px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1'
  };
  
  const footerSocialStyles = {
    display: 'flex',
    gap: '15px',
    marginTop: '20px'
  };
  
  const footerSocialIconStyles = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '4px',
    backgroundColor: '#333333',
    color: '#ffffff',
    fontSize: '16px',
    transition: 'background-color 0.2s ease'
  };
  
  const footerDividerStyles = {
    height: '1px',
    backgroundColor: '#333333',
    width: '100%',
    marginTop: '30px',
    marginBottom: '20px'
  };
  
  const footerBottomStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '20px'
  };
  
  const footerCopyrightStyles = {
    fontSize: '14px',
    color: '#999999'
  };
  
  const footerTermsStyles = {
    display: 'flex',
    gap: '20px'
  };
  
  // Tablet Styles
  const tabletFooterMainStyles = {
    ...footerMainStyles,
    gap: '30px'
  };
  
  const tabletFooterColumnStyles = {
    ...footerColumnStyles,
    minWidth: '160px'
  };
  
  // Mobile Styles
  const mobileFooterMainStyles = {
    flexDirection: 'column',
    gap: '40px'
  };
  
  const mobileFooterBottomStyles = {
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '20px'
  };
  
  const mobileFooterTermsStyles = {
    justifyContent: 'center'
  };
  
  // Newsletter Form Styles
  const newsletterFormStyles = {
    display: 'flex',
    position: 'relative',
    width: '100%'
  };
  
  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(footerSectionClass, footerSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContainerClass, footerContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(footerMainClass, footerMainStyles);
    newRegistry[DEVICES.DESKTOP].set(footerColumnClass, footerColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLogoContainerClass, footerLogoContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLogoClass, footerLogoStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLogoTextClass, footerLogoTextStyles);
    newRegistry[DEVICES.DESKTOP].set(footerDescriptionClass, footerDescriptionStyles);
    newRegistry[DEVICES.DESKTOP].set(footerAppLinksClass, footerAppLinksStyles);
    newRegistry[DEVICES.DESKTOP].set(footerHeadingClass, footerHeadingStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLinkClass, footerLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLinkListClass, footerLinkListStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContactClass, footerContactStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContactItemClass, footerContactItemStyles);
    newRegistry[DEVICES.DESKTOP].set(footerNewsletterClass, footerNewsletterStyles);
    newRegistry[DEVICES.DESKTOP].set(footerInputClass, footerInputStyles);
    newRegistry[DEVICES.DESKTOP].set(footerButtonClass, footerButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(footerSocialClass, footerSocialStyles);
    newRegistry[DEVICES.DESKTOP].set(footerSocialIconClass, footerSocialIconStyles);
    newRegistry[DEVICES.DESKTOP].set(footerBottomClass, footerBottomStyles);
    newRegistry[DEVICES.DESKTOP].set(footerDividerClass, footerDividerStyles);
    newRegistry[DEVICES.DESKTOP].set(footerCopyrightClass, footerCopyrightStyles);
    newRegistry[DEVICES.DESKTOP].set(footerTermsClass, footerTermsStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(footerMainClass, tabletFooterMainStyles);
    newRegistry[DEVICES.TABLET].set(footerColumnClass, tabletFooterColumnStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(footerMainClass, mobileFooterMainStyles);
    newRegistry[DEVICES.MOBILE].set(footerBottomClass, mobileFooterBottomStyles);
    newRegistry[DEVICES.MOBILE].set(footerTermsClass, mobileFooterTermsStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    footerSectionClass,
    footerContainerClass,
    footerMainClass,
    footerColumnClass,
    footerLogoContainerClass,
    footerLogoClass,
    footerLogoTextClass,
    footerDescriptionClass,
    footerAppLinksClass,
    footerHeadingClass,
    footerLinkClass,
    footerLinkListClass,
    footerContactClass,
    footerContactItemClass,
    footerNewsletterClass,
    footerInputClass,
    footerButtonClass,
    footerSocialClass,
    footerSocialIconClass,
    footerBottomClass,
    footerDividerClass,
    footerCopyrightClass,
    footerTermsClass
  ]));

  // Generate IDs for elements
  const footerSectionId = Date.now();
  const containerId = Date.now() + 1;
  const mainId = Date.now() + 2;
  
  // Left column - logo and company info
  const column1Id = Date.now() + 3;
  const logoContainerId = Date.now() + 4;
  const logoId = Date.now() + 5;
  const logoTextId = Date.now() + 6;
  const descriptionId = Date.now() + 7;
  const appLinksId = Date.now() + 8;
  const googlePlayId = Date.now() + 9;
  const googlePlayImgId = Date.now() + 10;
  const appStoreId = Date.now() + 11;
  const appStoreImgId = Date.now() + 12;
  
  // Information Links
  const column2Id = Date.now() + 13;
  const infoHeadingId = Date.now() + 14;
  const infoListId = Date.now() + 15;
  const infoLink1Id = Date.now() + 16;
  const infoLink2Id = Date.now() + 17;
  const infoLink3Id = Date.now() + 18;
  const infoLink4Id = Date.now() + 19;
  const infoLink5Id = Date.now() + 20;
  
  // Support Links
  const column3Id = Date.now() + 21;
  const supportHeadingId = Date.now() + 22;
  const supportListId = Date.now() + 23;
  const supportLink1Id = Date.now() + 24;
  const supportLink2Id = Date.now() + 25;
  const supportLink3Id = Date.now() + 26;
  const supportLink4Id = Date.now() + 27;
  const supportLink5Id = Date.now() + 28;
  
  // Contact Info
  const column4Id = Date.now() + 29;
  const contactHeadingId = Date.now() + 30;
  const contactListId = Date.now() + 31;
  const contactItem1Id = Date.now() + 32;
  const contactItem2Id = Date.now() + 33;
  const contactItem3Id = Date.now() + 34;
  const socialId = Date.now() + 35;
  const socialIcon1Id = Date.now() + 36;
  const socialIcon2Id = Date.now() + 37;
  const socialIcon3Id = Date.now() + 38;
  
  // Newsletter
  const column5Id = Date.now() + 39;
  const newsletterHeadingId = Date.now() + 40;
  const newsletterDescId = Date.now() + 41;
  const newsletterFormId = Date.now() + 42;
  const newsletterInputId = Date.now() + 43;
  const newsletterButtonId = Date.now() + 44;
  const newsletterNoteId = Date.now() + 45;
  
  // Bottom section
  const dividerId = Date.now() + 46;
  const bottomId = Date.now() + 47;
  const copyrightId = Date.now() + 48;
  const termsId = Date.now() + 49;
  const termsLink1Id = Date.now() + 50;
  const termsLink2Id = Date.now() + 51;
  
  // Generate additional IDs for contact item elements
  const addressIconId = Date.now() + 52;
  const addressTextId = Date.now() + 53;
  const phoneIconId = Date.now() + 54;
  const phoneTextId = Date.now() + 55;
  const emailIconId = Date.now() + 56;
  const emailTextId = Date.now() + 57;

  // Generate IDs for social media icons
  const youtubeIconId = Date.now() + 58;
  const facebookIconId = Date.now() + 59;
  const twitterIconId = Date.now() + 60;
  
  // Create the elements
  const newElements = [
    // Main Footer Section
    {
      id: footerSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Footer Section',
      parentId: null,
      className: footerSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'footer-section',
        'data-component': 'section',
        'data-section-type': 'footer',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Container',
      parentId: footerSectionId,
      hierarchyName: "Footer Container",
      className: footerContainerClass,
      styles: {},
      children: [mainId, dividerId, bottomId],
      text: '',
      attributes: {
        'data-element': 'footer-container'
      }
    },
    
    // Main content with columns
    {
      id: mainId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Main',
      parentId: containerId,
      hierarchyName: "Footer Main",
      className: footerMainClass,
      styles: {},
      children: [column1Id, column2Id, column3Id, column4Id, column5Id],
      text: '',
      attributes: {
        'data-element': 'footer-main'
      }
    },
    
    // Column 1 - Logo and Company Info
    {
      id: column1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Column 1',
      parentId: mainId,
      hierarchyName: "Footer Column 1",
      className: footerColumnClass,
      styles: {},
      children: [logoContainerId, descriptionId, appLinksId],
      text: '',
      attributes: {
        'data-element': 'footer-column'
      }
    },
    
    // Logo Container
    {
      id: logoContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Logo Container',
      parentId: column1Id,
      hierarchyName: "Logo Container",
      className: footerLogoContainerClass,
      styles: {},
      children: [logoId],
      text: '',
      attributes: {
        'data-element': 'logo-container'
      }
    },
    
    // Logo
    {
      id: logoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Logo',
      parentId: logoContainerId,
      hierarchyName: "Logo",
      className: footerLogoClass,
      styles: {},
      children: [logoTextId],
      text: '',
      attributes: {
        'data-element': 'footer-logo'
      }
    },
    
    // Logo Text
    {
      id: logoTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Logo Text',
      parentId: logoId,
      hierarchyName: "Logo Text",
      className: footerLogoTextClass,
      styles: {},
      children: [],
      text: 'Mezan',
      attributes: {
        'data-element': 'logo-text'
      }
    },
    
    // Description
    {
      id: descriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Company Description',
      parentId: column1Id,
      hierarchyName: "Company Description",
      className: footerDescriptionClass,
      styles: {},
      children: [],
      text: 'Duis ultricies libero sit amet aliquam fermentum. Nunc tincidunt mollis dui in tempor. Maecenas ut leo vitae purus condimentum dignissim.',
      attributes: {
        'data-element': 'company-description'
      }
    },
    
    // App Links
    {
      id: appLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'App Links',
      parentId: column1Id,
      hierarchyName: "App Links",
      className: footerAppLinksClass,
      styles: {},
      children: [googlePlayId, appStoreId],
      text: '',
      attributes: {
        'data-element': 'app-links'
      }
    },
    
    // Google Play Link Container
    {
      id: googlePlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Google Play',
      parentId: appLinksId,
      hierarchyName: "Google Play",
      styles: {
        display: 'block',
        width: '110px'
      },
      children: [googlePlayImgId],
      text: '',
      attributes: {
        'data-element': 'google-play-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer'
      }
    },
    
    // Google Play Image
    {
      id: googlePlayImgId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Google Play Image',
      parentId: googlePlayId,
      hierarchyName: "Google Play Image",
      styles: {
        width: '100%',
        height: 'auto'
      },
      src: 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png',
      attributes: {
        'data-element': 'google-play-image',
        'alt': 'Get it on Google Play'
      }
    },
    
    // App Store Link Container
    {
      id: appStoreId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'App Store',
      parentId: appLinksId,
      hierarchyName: "App Store",
      styles: {
        display: 'block',
        width: '90px',
        display: 'flex',
      },
      children: [appStoreImgId],
      text: '',
      attributes: {
        'data-element': 'app-store-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer'
      }
    },

    // App Store Image
    {
      id: appStoreImgId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'App Store Image',
      parentId: appStoreId,
      hierarchyName: "App Store Image",
      styles: {
        width: '100%',
        height: 'auto'
      },
      src: 'https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg',
      attributes: {
        'data-element': 'app-store-image',
        'alt': 'Download on the App Store'
      }
    },
  
    // Column 2 - Information Links
    {
      id: column2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Column 2',
      parentId: mainId,
      hierarchyName: "Footer Column 2",
      className: footerColumnClass,
      styles: {},
      children: [infoHeadingId, infoListId],
      text: '',
      attributes: {
        'data-element': 'footer-column'
      }
    },
    
    // Information Heading
    {
      id: infoHeadingId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Information Heading',
      parentId: column2Id,
      hierarchyName: "Information Heading",
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Information',
      attributes: {
        'data-element': 'footer-heading'
      }
    },
    
    // Information Links List
    {
      id: infoListId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Information Links',
      parentId: column2Id,
      hierarchyName: "Information Links",
      className: footerLinkListClass,
      styles: {},
      children: [infoLink1Id, infoLink2Id, infoLink3Id, infoLink4Id, infoLink5Id],
      text: '',
      attributes: {
        'data-element': 'footer-links'
      }
    },
    
    // Information Link 1
    {
      id: infoLink1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Information Link 1',
      parentId: infoListId,
      hierarchyName: "Information Link 1",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Request Service',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Information Link 2
    {
      id: infoLink2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Information Link 2',
      parentId: infoListId,
      hierarchyName: "Information Link 2",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Commercial Plumbing',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Information Link 3
    {
      id: infoLink3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Information Link 3',
      parentId: infoListId,
      hierarchyName: "Information Link 3",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Our Work',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Information Link 4
    {
      id: infoLink4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Information Link 4',
      parentId: infoListId,
      hierarchyName: "Information Link 4",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'What We Do',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Information Link 5
    {
      id: infoLink5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Information Link 5',
      parentId: infoListId,
      hierarchyName: "Information Link 5",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Reviews',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Column 3 - Support Links
    {
      id: column3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Column 3',
      parentId: mainId,
      hierarchyName: "Footer Column 3",
      className: footerColumnClass,
      styles: {},
      children: [supportHeadingId, supportListId],
      text: '',
      attributes: {
        'data-element': 'footer-column'
      }
    },
    
    // Support Heading
    {
      id: supportHeadingId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Support Heading',
      parentId: column3Id,
      hierarchyName: "Support Heading",
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Support',
      attributes: {
        'data-element': 'footer-heading'
      }
    },
    
    // Support Links List
    {
      id: supportListId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Support Links',
      parentId: column3Id,
      hierarchyName: "Support Links",
      className: footerLinkListClass,
      styles: {},
      children: [supportLink1Id, supportLink2Id, supportLink3Id, supportLink4Id, supportLink5Id],
      text: '',
      attributes: {
        'data-element': 'footer-links'
      }
    },
    
    // Support Link 1
    {
      id: supportLink1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Support Link 1',
      parentId: supportListId,
      hierarchyName: "Support Link 1",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'About',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Support Link 2
    {
      id: supportLink2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Support Link 2',
      parentId: supportListId,
      hierarchyName: "Support Link 2",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Careers',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Support Link 3
    {
      id: supportLink3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Support Link 3',
      parentId: supportListId,
      hierarchyName: "Support Link 3",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Newsletter',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Support Link 4
    {
      id: supportLink4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Support Link 4',
      parentId: supportListId,
      hierarchyName: "Support Link 4",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Privacy Policy',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Support Link 5
    {
      id: supportLink5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Support Link 5',
      parentId: supportListId,
      hierarchyName: "Support Link 5",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Terms Of Use',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
  
    // Column 4 - Quick Contact
    {
      id: column4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Column 4',
      parentId: mainId,
      hierarchyName: "Footer Column 4",
      className: footerColumnClass,
      styles: {},
      children: [contactHeadingId, contactListId, socialId],
      text: '',
      attributes: {
        'data-element': 'footer-column'
      }
    },
    
    // Contact Heading
    {
      id: contactHeadingId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Contact Heading',
      parentId: column4Id,
      hierarchyName: "Contact Heading",
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Quick Contact',
      attributes: {
        'data-element': 'footer-heading'
      }
    },
    
    // Contact List Container
    {
      id: contactListId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Information',
      parentId: column4Id,
      hierarchyName: "Contact Information",
      className: footerContactClass,
      styles: {},
      children: [contactItem1Id, contactItem2Id, contactItem3Id],
      text: '',
      attributes: {
        'data-element': 'contact-info'
      }
    },

    // Contact Item - Address
    {
      id: contactItem1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address',
      parentId: contactListId,
      hierarchyName: "Address",
      className: footerContactItemClass,
      styles: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px'
      },
      children: [addressIconId, addressTextId],
      text: '',
      attributes: {
        'data-element': 'contact-item'
      }
    },

    // Contact Item - Phone
    {
      id: contactItem2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone',
      parentId: contactListId,
      hierarchyName: "Phone",
      className: footerContactItemClass,
      styles: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px'
      },
      children: [phoneIconId, phoneTextId],
      text: '',
      attributes: {
        'data-element': 'contact-item'
      }
    },

    // Contact Item - Email
    {
      id: contactItem3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email',
      parentId: contactListId,
      hierarchyName: "Email",
      className: footerContactItemClass,
      styles: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px'
      },
      children: [emailIconId, emailTextId],
      text: '',
      attributes: {
        'data-element': 'contact-item'
      }
    },

    // Address Icon
    {
      id: addressIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Address Icon',
      parentId: contactItem1Id,
      hierarchyName: "Address Icon",
      styles: {
        width: '16px',
        height: '16px',
        marginTop: '4px',
        flexShrink: '0'
      },
      src: 'https://api.iconify.design/material-symbols:location-on.svg?color=%23ffd700',
      attributes: {
        'data-element': 'contact-icon',
        'alt': 'Location Icon'
      }
    },

    // Address Text
    {
      id: addressTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Address Text',
      parentId: contactItem1Id,
      hierarchyName: "Address Text",
      styles: {
        flex: '1'
      },
      children: [],
      text: 'No: 58 A, East Madison Street, Baltimore, MD, USA 4508',
      attributes: {
        'data-element': 'contact-text'
      }
    },

    // Phone Icon
    {
      id: phoneIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Phone Icon',
      parentId: contactItem2Id,
      hierarchyName: "Phone Icon",
      styles: {
        width: '16px',
        height: '16px',
        marginTop: '4px',
        flexShrink: '0'
      },
      src: 'https://api.iconify.design/material-symbols:call.svg?color=%23ffd700',
      attributes: {
        'data-element': 'contact-icon',
        'alt': 'Phone Icon'
      }
    },

    // Phone Text
    {
      id: phoneTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Phone Text',
      parentId: contactItem2Id,
      hierarchyName: "Phone Text",
      styles: {
        flex: '1'
      },
      children: [],
      text: '+000 123 456 789',
      attributes: {
        'data-element': 'contact-text'
      }
    },

    // Email Icon
    {
      id: emailIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Email Icon',
      parentId: contactItem3Id,
      hierarchyName: "Email Icon",
      styles: {
        width: '16px',
        height: '16px',
        marginTop: '4px',
        flexShrink: '0'
      },
      src: 'https://api.iconify.design/material-symbols:mail-outline.svg?color=%23ffd700',
      attributes: {
        'data-element': 'contact-icon',
        'alt': 'Email Icon'
      }
    },

    // Email Text
    {
      id: emailTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Email Text',
      parentId: contactItem3Id,
      hierarchyName: "Email Text",
      styles: {
        flex: '1'
      },
      children: [],
      text: 'info@example.com',
      attributes: {
        'data-element': 'contact-text'
      }
    },

    // Social Icons Container
    {
      id: socialId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Social Icons',
      parentId: column4Id,
      hierarchyName: "Social Icons",
      className: footerSocialClass,
      styles: {},
      children: [socialIcon1Id, socialIcon2Id, socialIcon3Id],
      text: '',
      attributes: {
        'data-element': 'social-icons'
      }
    },
    
    // YouTube Link
    {
      id: socialIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'YouTube',
      parentId: socialId,
      hierarchyName: "YouTube",
      className: footerSocialIconClass,
      styles: {},
      children: [youtubeIconId],
      text: '',
      attributes: {
        'data-element': 'social-icon',
        'href': '#',
        'aria-label': 'YouTube'
      }
    },

    // YouTube Icon
    {
      id: youtubeIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'YouTube Icon',
      parentId: socialIcon1Id,
      hierarchyName: "YouTube Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:youtube.svg?color=%23ffffff',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'YouTube'
      }
    },
    
    // Facebook Link
    {
      id: socialIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Facebook',
      parentId: socialId,
      hierarchyName: "Facebook",
      className: footerSocialIconClass,
      styles: {},
      children: [facebookIconId],
      text: '',
      attributes: {
        'data-element': 'social-icon',
        'href': '#',
        'aria-label': 'Facebook'
      }
    },

    // Facebook Icon
    {
      id: facebookIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Facebook Icon',
      parentId: socialIcon2Id,
      hierarchyName: "Facebook Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:facebook.svg?color=%23ffffff',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Facebook'
      }
    },
    
    // Twitter Link
    {
      id: socialIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Twitter',
      parentId: socialId,
      hierarchyName: "Twitter",
      className: footerSocialIconClass,
      styles: {},
      children: [twitterIconId],
      text: '',
      attributes: {
        'data-element': 'social-icon',
        'href': '#',
        'aria-label': 'Twitter'
      }
    },

    // Twitter Icon
    {
      id: twitterIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Twitter Icon',
      parentId: socialIcon3Id,
      hierarchyName: "Twitter Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:twitter.svg?color=%23ffffff',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Twitter'
      }
    },
    
    // Column 5 - Newsletter
    {
      id: column5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Column 5',
      parentId: mainId,
      hierarchyName: "Footer Column 5",
      className: footerColumnClass,
      styles: {},
      children: [newsletterHeadingId, newsletterDescId, newsletterFormId, newsletterNoteId],
      text: '',
      attributes: {
        'data-element': 'footer-column'
      }
    },
    
    // Newsletter Heading
    {
      id: newsletterHeadingId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Newsletter Heading',
      parentId: column5Id,
      hierarchyName: "Newsletter Heading",
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Newsletter',
      attributes: {
        'data-element': 'footer-heading'
      }
    },
    
    // Newsletter Description
    {
      id: newsletterDescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Newsletter Description',
      parentId: column5Id,
      hierarchyName: "Newsletter Description",
      className: footerDescriptionClass,
      styles: {},
      children: [],
      text: 'Sunc tincidunt mollis dui in tempor. Duis ultricies libero sit amet.',
      attributes: {
        'data-element': 'newsletter-description'
      }
    },
    
    // Newsletter Form
    {
      id: newsletterFormId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Newsletter Form',
      parentId: column5Id,
      hierarchyName: "Newsletter Form",
      styles: newsletterFormStyles,
      children: [newsletterInputId, newsletterButtonId],
      text: '',
      attributes: {
        'data-element': 'newsletter-form'
      }
    },
    
    // Newsletter Input
    {
      id: newsletterInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Newsletter Input',
      parentId: newsletterFormId,
      hierarchyName: "Newsletter Input",
      className: footerInputClass,
      styles: footerInputStyles,
      children: [],
      text: '',
      attributes: {
        'data-element': 'newsletter-input',
        'type': 'email',
        'placeholder': 'Enter Your Email Id'
      }
    },
    
    // Newsletter Button
    {
      id: newsletterButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Newsletter Button',
      parentId: newsletterFormId,
      hierarchyName: "Newsletter Button",
      className: footerButtonClass,
      styles: footerButtonStyles,
      children: [],
      text: '>',
      attributes: {
        'data-element': 'newsletter-button',
        'type': 'submit',
        'aria-label': 'Submit'
      }
    },
    
    // Newsletter Note
    {
      id: newsletterNoteId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Newsletter Note',
      parentId: column5Id,
      hierarchyName: "Newsletter Note",
      styles: {
        fontSize: '13px',
        color: '#999999',
        marginTop: '10px'
      },
      children: [],
      text: 'We don\'t spam. Unsubscription any time.',
      attributes: {
        'data-element': 'newsletter-note'
      }
    },
    
    // Divider
    {
      id: dividerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Divider',
      parentId: containerId,
      hierarchyName: "Footer Divider",
      className: footerDividerClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'footer-divider'
      }
    },
    
    // Bottom Section
    {
      id: bottomId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Bottom',
      parentId: containerId,
      hierarchyName: "Footer Bottom",
      className: footerBottomClass,
      styles: {},
      children: [copyrightId, termsId],
      text: '',
      attributes: {
        'data-element': 'footer-bottom'
      }
    },
    
    // Copyright
    {
      id: copyrightId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Copyright',
      parentId: bottomId,
      hierarchyName: "Copyright",
      className: footerCopyrightClass,
      styles: {},
      children: [],
      text: `© ${new Date().getFullYear()} WedesignTech All rights Reserved.`,
      attributes: {
        'data-element': 'footer-copyright'
      }
    },
    
    // Terms Links
    {
      id: termsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Terms Links',
      parentId: bottomId,
      hierarchyName: "Terms Links",
      className: footerTermsClass,
      styles: {},
      children: [termsLink1Id, termsLink2Id],
      text: '',
      attributes: {
        'data-element': 'footer-terms'
      }
    },
    
    // Terms Link 1
    {
      id: termsLink1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Privacy Policy',
      parentId: termsId,
      hierarchyName: "Privacy Policy",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Privacy Policy',
      attributes: {
        'data-element': 'footer-term-link',
        'href': '#'
      }
    },
    
    // Terms Link 2
    {
      id: termsLink2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Terms & Condition',
      parentId: termsId,
      hierarchyName: "Terms & Condition",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Terms & Condition',
      attributes: {
        'data-element': 'footer-term-link',
        'href': '#'
      }
    }
  ];
  
  return {
    footerSection: newElements[0],
    elements: newElements
  };
};

export default createFooterElements; 